import React, { useEffect } from 'react';
import {
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useClientsManagementController } from "../context/clients-management-controller";
import { useClientsManagementState } from "../context/clients-management-context";
import MaskedTextField from '../../../components/masked-text-field';
import DefaultUploadFile from '../../../components/default-upload-file';

const ClientsManagementForm: React.FC = () => {
    const { handleFormChange, handleAddressChange, handleContactChange, loadStates, loadCities, updateAddress, setBrandLogo } = useClientsManagementController();
    const { clientForm, listStateResponse, listCityResponse, brandLogo } = useClientsManagementState();

    const handleFieldChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        handleFormChange(field, event.target.value);
    };

    const handleSelectChange = (field: string) => (event: any) => {
        handleFormChange(field, event.target.value);
    };

    const handleCheckboxChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        handleFormChange(field, event.target.checked);
    };

    const handleAddressSelectChange = (field: string) => (event: any) => {
        handleAddressChange(field, event.target.value);
    };

    const handleAddressFieldChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        handleAddressChange(field, event.target.value);
    };

    const handleContactFieldChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        handleContactChange(field, event.target.value);
    };

    const handleStateChange = (event: any) => {
        const stateId = Number(event.target.value);

        // Update both stateId and cityId at once to avoid race conditions
        updateAddress({
            stateId: stateId,
            cityId: 0 // Reset city when state changes
        });

        if (stateId && stateId !== 0) {
            loadCities(stateId);
        }
    };

    useEffect(() => {
        loadStates();
    }, []);

    return (
        <Grid container spacing={3}>
            {/* Informações Básicas */}
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                    Informações Básicas
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Nome"
                    value={clientForm?.name || ''}
                    onChange={handleFieldChange('name')}
                    required
                />
            </Grid>

            <Grid item xs={10}>
                <TextField
                    fullWidth
                    label="Razão Social"
                    value={clientForm?.corporateName || ''}
                    onChange={handleFieldChange('corporateName')}
                    required
                />
            </Grid>

            <Grid item xs={2}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={clientForm?.active ?? true}
                            onChange={handleCheckboxChange('active')}
                            color="primary"
                        />
                    }
                    label="Ativo"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <MaskedTextField
                    maskType="cnpj"
                    label="CNPJ"
                    id="input-document"
                    name="input-document"
                    value={clientForm?.document || ''}
                    onChange={handleFieldChange('document')}
                    fullWidth
                />
            </Grid>

            <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                    <InputLabel>Tipo de Cliente</InputLabel>
                    <Select
                        value={clientForm?.entityType || 0}
                        onChange={handleSelectChange('entityType')}
                        label="Tipo de Clientes"
                    >
                        <MenuItem value={1}>Prefeitura Municipal</MenuItem>
                        <MenuItem value={2}>Câmara Municipal</MenuItem>
                    </Select>
                </FormControl>
            </Grid>


            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Website"
                    value={clientForm?.webPage || ''}
                    onChange={handleFieldChange('webPage')}
                    type="url"
                />
            </Grid>

            <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" component="label" sx={{ mb: 1, display: 'block' }}>
                        Logomarca
                    </Typography>
                    <DefaultUploadFile
                        props={{
                            id: "upload-brand-logo",
                            setFileUpload: setBrandLogo,
                            file: brandLogo ?? null,
                            extensions: ["png"],
                            maxFileSizeMb: 2
                        }}
                    />
                </Box>
            </Grid>

            {/* Links Institucionais */}
            <Grid item xs={12}>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Links Institucionais</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Portal de Servidores"
                                    value={clientForm?.desUrlServidores || ''}
                                    onChange={handleFieldChange('desUrlServidores')}
                                    type="url"
                                    placeholder="https://servidores.exemplo.gov.br"
                                />
                            </Grid>
                                    <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Portal da Transparência"
                                    value={clientForm?.desUrlTransparencia || ''}
                                    onChange={handleFieldChange('desUrlTransparencia')}
                                    type="url"
                                    placeholder="https://transparencia.exemplo.gov.br"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Portal da Ouvidoria"
                                    value={clientForm?.desUrlOuvidoria || ''}
                                    onChange={handleFieldChange('desUrlOuvidoria')}
                                    type="url"
                                    placeholder="https://ouvidoria.exemplo.gov.br"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Portal de Licitações"
                                    value={clientForm?.desUrlLicitacoes || ''}
                                    onChange={handleFieldChange('desUrlLicitacoes')}
                                    type="url"
                                    placeholder="https://licitacoes.exemplo.gov.br"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Portal do Diário Oficial"
                                    value={clientForm?.desUrlDiarioOficial || ''}
                                    onChange={handleFieldChange('desUrlDiarioOficial')}
                                    type="url"
                                    placeholder="https://diariooficial.exemplo.gov.br"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Portal SIC (Serviço de Informação ao Cidadão)"
                                    value={clientForm?.desUrlSic || ''}
                                    onChange={handleFieldChange('desUrlSic')}
                                    type="url"
                                    placeholder="https://sic.exemplo.gov.br"
                                />
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>

            {/* Redes Sociais */}
            <Grid item xs={12}>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Redes Sociais (Perfis)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Instagram"
                                    value={clientForm?.instagramProfile || ''}
                                    onChange={handleFieldChange('instagramProfile')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Facebook"
                                    value={clientForm?.facebookProfile || ''}
                                    onChange={handleFieldChange('facebookProfile')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Twitter"
                                    value={clientForm?.twitterProfile || ''}
                                    onChange={handleFieldChange('twitterProfile')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="YouTube"
                                    value={clientForm?.youtubeProfile || ''}
                                    onChange={handleFieldChange('youtubeProfile')}
                                />
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>

            {/* Endereço */}
            <Grid item xs={12}>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Endereço</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12}>
                                <TextField
                                    fullWidth
                                    label="Logradouro"
                                    value={clientForm?.address?.street || ''}
                                    onChange={handleAddressFieldChange('street')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    label="Número"
                                    value={clientForm?.address?.number || ''}
                                    onChange={handleAddressFieldChange('number')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={9}>
                                <TextField
                                    fullWidth
                                    label="Complemento"
                                    value={clientForm?.address?.complement || ''}
                                    onChange={handleAddressFieldChange('complement')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Bairro"
                                    value={clientForm?.address?.neighborhood || ''}
                                    onChange={handleAddressFieldChange('neighborhood')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MaskedTextField
                                    maskType="cep"
                                    label="CEP"
                                    id="input-postalCode"
                                    name="input-postalCode"
                                    value={clientForm?.address?.postalCode || ''}
                                    onChange={handleAddressFieldChange('postalCode')}
                                    fullWidth
                                />

                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Estado</InputLabel>
                                    <Select
                                        value={clientForm?.address?.stateId || 0}
                                        onChange={handleStateChange}
                                        label="Estado"
                                        displayEmpty
                                    >
                                        <MenuItem value={0}>Selecione um estado</MenuItem>
                                        {listStateResponse?.map((state) => (
                                            <MenuItem key={state.id} value={state.id}>
                                                {state.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Cidade</InputLabel>
                                    <Select
                                        value={clientForm?.address?.cityId || 0}
                                        onChange={handleAddressSelectChange('cityId')}
                                        label="Cidade"
                                        disabled={!clientForm?.address?.stateId || clientForm?.address?.stateId === 0}
                                    >
                                        <MenuItem value={0}>
                                            {!clientForm?.address?.stateId || clientForm?.address?.stateId === 0
                                                ? "Selecione um estado primeiro"
                                                : "Selecione uma cidade"}
                                        </MenuItem>
                                        {listCityResponse?.map((city) => (
                                            <MenuItem key={city.id} value={city.id}>
                                                {city.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>

            {/* Contato */}
            <Grid item xs={12}>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Contato</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="E-mail"
                                    type="email"
                                    value={clientForm?.contact?.email || ''}
                                    onChange={handleContactFieldChange('email')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MaskedTextField
                                    maskType="phone"
                                    label="Telefone"
                                    id="input-phone"
                                    name="input-phone"
                                    fullWidth
                                    value={clientForm?.contact?.phone || ''}
                                    onChange={handleContactFieldChange('phone')}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Website"
                                    value={clientForm?.contact?.website || ''}
                                    onChange={handleContactFieldChange('website')}
                                    type="url"
                                />
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>
        </Grid>
    );
};

export default ClientsManagementForm;